import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiClock, FiMapPin, FiCheck, FiTruck } = FiIcons;

function AIServiceScheduler() {
  const [serviceRequest, setServiceRequest] = useState('');
  const [appointment, setAppointment] = useState(null);
  const [isBooking, setIsBooking] = useState(false);

  const handleBooking = () => {
    setIsBooking(true);
    setTimeout(() => {
      setAppointment({
        service: 'Winter Tire Installation',
        date: 'Friday, March 15th',
        time: '2:30 PM AST',
        duration: '90 minutes',
        technician: 'Mike Johnson',
        shuttleAvailable: true
      });
      setIsBooking(false);
    }, 2000);
  };

  const quickServices = [
    { name: 'Oil Change', duration: '30 min', price: '$65.99 CAD' },
    { name: 'Winter Tire Installation', duration: '90 min', price: '$89.99 CAD' },
    { name: 'Brake Inspection', duration: '60 min', price: '$99.99 CAD' },
    { name: 'MVI Inspection', duration: '90 min', price: '$149.99 CAD' }
  ];

  return (
    <section id="service" className="py-20 bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold text-white mb-4">
            Halifax AI Service Scheduler
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Book your Halifax service appointment in natural language. Our AI handles the rest for Maritime drivers.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Booking Interface */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-gray-800 rounded-xl p-8"
          >
            <div className="flex items-center space-x-3 mb-6">
              <SafeIcon icon={FiCalendar} className="w-6 h-6 text-blue-400" />
              <h3 className="text-xl font-semibold text-white">Schedule Halifax Service</h3>
            </div>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  What service do you need in Halifax?
                </label>
                <textarea
                  value={serviceRequest}
                  onChange={(e) => setServiceRequest(e.target.value)}
                  placeholder="Try: 'Book me winter tire installation Friday afternoon' or 'I need brake service next week in Halifax'"
                  className="w-full p-4 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none resize-none h-24"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                {quickServices.map((service, index) => (
                  <button
                    key={index}
                    onClick={() => setServiceRequest(`Book ${service.name} appointment in Halifax`)}
                    className="p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors text-left"
                  >
                    <h4 className="text-white font-medium">{service.name}</h4>
                    <p className="text-gray-400 text-sm">{service.duration} • {service.price}</p>
                  </button>
                ))}
              </div>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleBooking}
                disabled={!serviceRequest}
                className="w-full px-6 py-4 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiCalendar} className="w-5 h-5" />
                <span>Book with Halifax AI</span>
              </motion.button>
            </div>
          </motion.div>

          {/* Appointment Confirmation */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-gray-800 rounded-xl p-8"
          >
            {isBooking ? (
              <div className="flex flex-col items-center justify-center h-full space-y-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
                <p className="text-gray-300">AI is checking Halifax availability...</p>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div>
                </div>
              </div>
            ) : appointment ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="space-y-6"
              >
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiCheck} className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white">Halifax Appointment Confirmed</h3>
                    <p className="text-green-400">Confirmation sent to your email & phone</p>
                  </div>
                </div>
                <div className="bg-gray-700 rounded-lg p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">Service:</span>
                    <span className="text-white font-semibold">{appointment.service}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">Date:</span>
                    <span className="text-white">{appointment.date}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">Time:</span>
                    <span className="text-white">{appointment.time}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">Duration:</span>
                    <span className="text-white">{appointment.duration}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">Technician:</span>
                    <span className="text-white">{appointment.technician}</span>
                  </div>
                </div>
                {appointment.shuttleAvailable && (
                  <div className="bg-blue-600/20 border border-blue-500/30 rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                      <SafeIcon icon={FiTruck} className="w-5 h-5 text-blue-400" />
                      <div>
                        <h4 className="text-white font-medium">Halifax Shuttle Service Available</h4>
                        <p className="text-blue-200 text-sm">Complimentary pickup & drop-off within Halifax Regional Municipality</p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex space-x-4">
                  <button className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                    Add to Calendar
                  </button>
                  <button className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors">
                    Modify
                  </button>
                </div>
              </motion.div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <SafeIcon icon={FiClock} className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">Describe your Halifax service needs to get started</p>
                  <div className="mt-6 space-y-2">
                    <div className="flex items-center text-gray-300 text-sm">
                      <SafeIcon icon={FiMapPin} className="w-4 h-4 mr-2" />
                      <span>Halifax Service Centre: 1234 Robie Street, Halifax, NS</span>
                    </div>
                    <div className="flex items-center text-gray-300 text-sm">
                      <SafeIcon icon={FiClock} className="w-4 h-4 mr-2" />
                      <span>Hours: Mon-Fri 7AM-6PM AST, Sat 8AM-4PM AST</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default AIServiceScheduler;